import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { auth, jobs, recommendations, candidates } from './utils/api';
import './App.css';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await auth.login(email, password);
      if (data.user.role === 'recruiter') {
        navigate('/recruiter/dashboard');
      } else {
        navigate('/company/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img src="/logo-full.png" alt="The Vault" style={{height: 
'200px', 
marginBottom: '10px'}} />
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="your@email.com" />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div className="demo-accounts">
          <p><strong>Demo Accounts:</strong></p>
          <p>Recruiter: recruiter@example.com / password123</p>
          <p>Company: contact@techventure.com / password123</p>
        </div>
      </div>
    </div>
  );
}

function CandidateDatabase() {
  const [candidatesList, setCandidatesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', location: '',
    currentSalary: '', expectedSalary: '', salaryCurrency: 'USD',
    startMonth: '', startYear: '',
    industry: '', currentRole: '', currentCompany: '',
    yearsOfExperience: '', linkedinUrl: '',
    education: '', qualifications: '', keywords: '', notes: ''
  });

  useEffect(() => { loadCandidates(); }, []);

  const loadCandidates = async () => {
    try {
      const data = await candidates.getAll();
      setCandidatesList(data);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, expectedStartDate: formData.startMonth && formData.startYear ? { month: formData.startMonth, year: parseInt(formData.startYear) } : undefined };
      if (editingId) { await candidates.update(editingId, payload); alert('Updated!'); }
      else { await candidates.create(payload); alert('Added!'); }
      setShowForm(false); setEditingId(null);
      setFormData({ name: '', email: '', phone: '', location: '', currentSalary: '', expectedSalary: '', salaryCurrency: 'USD', startMonth: '', startYear: '', industry: '', currentRole: '', currentCompany: '', yearsOfExperience: '', linkedinUrl: '', education: '', qualifications: '', keywords: '', notes: '' });
      loadCandidates();
    } catch (err) { alert('Error saving candidate'); }
  };

  const handleEdit = (c) => {
    setEditingId(c._id);
    setFormData({ name: c.name, email: c.email, phone: c.phone || '', location: c.location || '', currentSalary: c.currentSalary || '', expectedSalary: c.expectedSalary || '', salaryCurrency: c.salaryCurrency || 'USD', startMonth: c.expectedStartDate?.month || '', startYear: c.expectedStartDate?.year || '', industry: c.industry || '', currentRole: c.currentRole || '', currentCompany: c.currentCompany || '', yearsOfExperience: c.yearsOfExperience || '', linkedinUrl: c.linkedinUrl || '', education: c.education || '', qualifications: c.qualifications || '', keywords: c.keywords || '', notes: c.notes || '' });
    setSelectedCandidate(null); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this candidate?')) return;
    try { await candidates.delete(id); alert('Deleted!'); setSelectedCandidate(null); loadCandidates(); }
    catch (err) { alert('Error deleting candidate'); }
  };

  const filteredCandidates = candidatesList.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return ((c.name||'').toLowerCase().includes(q) || (c.currentRole||'').toLowerCase().includes(q) || (c.currentCompany||'').toLowerCase().includes(q) || (c.location||'').toLowerCase().includes(q) || (c.industry||'').toLowerCase().includes(q) || (c.keywords||'').toLowerCase().includes(q) || (c.qualifications||'').toLowerCase().includes(q) || (c.education||'').toLowerCase().includes(q) || (c.email||'').toLowerCase().includes(q));
  });

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'}}>
        <h1>Candidate Database</h1>
        <button className="btn-primary" onClick={() => { setShowForm(!showForm); setEditingId(null); }}>{showForm ? 'Cancel' : '+ Add Candidate'}</button>
      </div>

      <div style={{marginBottom: '24px'}}>
        <input type="text" placeholder="Search by name, role, company, location, industry, keywords..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{fontSize: '15px', padding: '14px 18px'}} />
        {searchQuery && <p style={{fontSize: '13px', color: '#64748b', marginTop: '8px'}}>{filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''} found</p>}
      </div>

      {showForm && (
        <div className="section">
          <h2 style={{marginBottom: '24px'}}>{editingId ? 'Edit Candidate' : 'Add New Candidate'}</h2>
          <form onSubmit={handleSubmit}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px'}}>
              <input required placeholder="Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              <input required type="email" placeholder="Email *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
              <input placeholder="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              <input placeholder="Location" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />
              <input placeholder="Current Role" value={formData.currentRole} onChange={(e) => setFormData({...formData, currentRole: e.target.value})} />
              <input placeholder="Current Company" value={formData.currentCompany} onChange={(e) => setFormData({...formData, currentCompany: e.target.value})} />
              <input type="number" placeholder="Years of Experience" value={formData.yearsOfExperience} onChange={(e) => setFormData({...formData, yearsOfExperience: e.target.value})} />
              <input placeholder="LinkedIn URL" value={formData.linkedinUrl} onChange={(e) => setFormData({...formData, linkedinUrl: e.target.value})} />
            </div>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginTop: '15px'}}>
              <input type="number" placeholder="Current Salary" value={formData.currentSalary} onChange={(e) => setFormData({...formData, currentSalary: e.target.value})} />
              <input type="number" placeholder="Expected Salary" value={formData.expectedSalary} onChange={(e) => setFormData({...formData, expectedSalary: e.target.value})} />
              <select value={formData.salaryCurrency} onChange={(e) => setFormData({...formData, salaryCurrency: e.target.value})}><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option><option value="AED">AED</option></select>
            </div>
            <div style={{marginTop: '15px'}}>
              <select value={formData.industry} onChange={(e) => setFormData({...formData, industry: e.target.value})}><option value="">Select Industry</option><option>Fintech</option><option>Proptech</option><option>Healthtech</option><option>Ecommerce</option><option>Ride Hailing</option><option>Foodtech</option><option>Other</option></select>
            </div>
            <textarea placeholder="Education" rows="3" value={formData.education} onChange={(e) => setFormData({...formData, education: e.target.value})} style={{marginTop: '15px'}} />
            <textarea placeholder="Qualifications" rows="3" value={formData.qualifications} onChange={(e) => setFormData({...formData, qualifications: e.target.value})} style={{marginTop: '15px'}} />
            <input placeholder="Keywords (comma-separated)" value={formData.keywords} onChange={(e) => setFormData({...formData, keywords: e.target.value})} style={{marginTop: '15px'}} />
            <textarea placeholder="Notes" rows="4" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} style={{marginTop: '15px'}} />
            <button type="submit" className="btn-primary" style={{marginTop: '20px'}}>{editingId ? 'Update' : 'Add'} Candidate</button>
          </form>
        </div>
      )}

      {selectedCandidate && (
        <div onClick={() => setSelectedCandidate(null)} style={{position: 'fixed', inset: 0, background: 'rgba(10,24,40,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px', backdropFilter: 'blur(4px)'}}>
          <div onClick={(e) => e.stopPropagation()} style={{background: 'white', borderRadius: '16px', maxWidth: '700px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '40px', boxShadow: '0 32px 64px rgba(0,0,0,0.2)'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #E2E8F0'}}>
              <div><h2 style={{marginBottom: '4px'}}>{selectedCandidate.name}</h2><p style={{color: '#475569', fontSize: '15px'}}>{selectedCandidate.currentRole}{selectedCandidate.currentCompany && ` at ${selectedCandidate.currentCompany}`}</p></div>
              <button onClick={() => setSelectedCandidate(null)} style={{background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer'}}>×</button>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px'}}>
              {selectedCandidate.email && <div><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Email</strong><p style={{marginTop: '4px'}}>{selectedCandidate.email}</p></div>}
              {selectedCandidate.phone && <div><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Phone</strong><p style={{marginTop: '4px'}}>{selectedCandidate.phone}</p></div>}
              {selectedCandidate.location && <div><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Location</strong><p style={{marginTop: '4px'}}>{selectedCandidate.location}</p></div>}
              {selectedCandidate.yearsOfExperience && <div><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Experience</strong><p style={{marginTop: '4px'}}>{selectedCandidate.yearsOfExperience} years</p></div>}
              {selectedCandidate.industry && <div><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Industry</strong><p style={{marginTop: '4px'}}>{selectedCandidate.industry}</p></div>}
              {selectedCandidate.linkedinUrl && <div><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>LinkedIn</strong><p style={{marginTop: '4px'}}><a href={selectedCandidate.linkedinUrl} target="_blank" rel="noopener noreferrer">{selectedCandidate.linkedinUrl}</a></p></div>}
            </div>
            {selectedCandidate.education && <div style={{marginBottom: '16px'}}><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Education</strong><p style={{marginTop: '4px', whiteSpace: 'pre-wrap'}}>{selectedCandidate.education}</p></div>}
            {selectedCandidate.qualifications && <div style={{marginBottom: '16px'}}><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Qualifications</strong><p style={{marginTop: '4px', whiteSpace: 'pre-wrap'}}>{selectedCandidate.qualifications}</p></div>}
            {selectedCandidate.keywords && <div style={{marginBottom: '16px'}}><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Keywords</strong><p style={{marginTop: '4px'}}>{selectedCandidate.keywords}</p></div>}
            {selectedCandidate.notes && <div style={{marginBottom: '24px'}}><strong style={{fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Notes</strong><p style={{marginTop: '4px', whiteSpace: 'pre-wrap'}}>{selectedCandidate.notes}</p></div>}
            <div style={{display: 'flex', gap: '12px', paddingTop: '20px', borderTop: '1px solid #E2E8F0'}}>
              <button className="btn-primary" onClick={() => handleEdit(selectedCandidate)}>Edit</button>
              <button className="btn-secondary" style={{color: '#B91C1C'}} onClick={() => handleDelete(selectedCandidate._id)}>Delete</button>
              <button className="btn-secondary" onClick={() => setSelectedCandidate(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="candidates-list">
        {filteredCandidates.length === 0 ? (
          <div style={{padding: '60px 20px', textAlign: 'center', background: 'white', borderRadius: '10px', border: '1px solid #E2E8F0'}}>
            <p style={{color: '#64748b', fontSize: '15px'}}>{searchQuery ? 'No candidates match your search' : 'No candidates yet. Add your first candidate!'}</p>
          </div>
        ) : (
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px'}}>
            {filteredCandidates.map(c => (
              <div key={c._id} onDoubleClick={() => setSelectedCandidate(c)} style={{background: 'white', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '20px', cursor: 'pointer', transition: 'all 0.2s ease'}}>
                <h3 style={{marginBottom: '4px'}}>{c.name}</h3>
                {c.currentRole && <p style={{fontSize: '14px', color: '#0A1828', fontWeight: '500', marginBottom: '2px'}}>{c.currentRole}</p>}
                {c.currentCompany && <p style={{fontSize: '13px', color: '#64748b'}}>{c.currentCompany}</p>}
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #F1F5F9', fontSize: '12px', color: '#64748b'}}>
                  {c.location && <span>📍 {c.location}</span>}
                  {c.yearsOfExperience && <span>⏱ {c.yearsOfExperience}y exp</span>}
                  {c.industry && <span>🏢 {c.industry}</span>}
                </div>
                <p style={{fontSize: '11px', color: '#94A3B8', marginTop: '12px', fontStyle: 'italic'}}>Double-click for full profile</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



function RecruiterDashboard() {
  const [stats, setStats] = useState(null);
  const [jobsList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, jobsData] = await Promise.all([jobs.getStats(), jobs.getAll({ status: 'open' })]);
      setStats(statsData);
      setJobsList(jobsData.slice(0, 5));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h1>Recruiter Dashboard</h1>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats?.openJobs || 0}</div>
          <div className="stat-label">Open Positions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.totalCompanies || 0}</div>
          <div className="stat-label">Portfolio Companies</div>
        </div>
      </div>
      <div className="section">
        <div className="section-header">
          <h2>Recent Open Positions</h2>
          <button className="btn-secondary" onClick={() => navigate('/recruiter/jobs')}>View All Jobs</button>
        </div>
        <div className="jobs-list">
          {jobsList.map(job => (
            <div key={job._id} className="job-card">
              <div className="job-header">
                <div>
                  <h3>{job.title}</h3>
                  <p className="company-name">{job.company?.name}</p>
                </div>
                {job.priority === 'urgent' && <span className="badge-urgent">Urgent</span>}
              </div>
              <div className="job-meta">
                <span>📍 {job.location}</span>
                <span>💼 {job.level}</span>
                <span>🏢 {job.department}</span>
                {job.salaryRange && <span>💰 {job.salaryRange.currency} {job.salaryRange.min?.toLocaleString()} - {job.salaryRange.max?.toLocaleString()}</span>}
                {job.expectedStartDate && <span>📅 Start: {job.expectedStartDate}</span>}
              </div>
              <button className="btn-primary btn-sm" onClick={() => navigate(`/recruiter/recommend/${job._id}`)}>
                Recommend Candidate
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RecruiterJobsList() {
  const [jobsList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const data = await jobs.getAll({ status: 'open' });
      setJobsList(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobsList.filter(job =>
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.company?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h1>All Open Positions</h1>
      <div className="search-bar">
        <input type="text" placeholder="Search jobs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>
      <div className="jobs-grid">
        {filteredJobs.map(job => (
          <div key={job._id} className="job-card">
            <div className="job-header">
              <div>
                <h3>{job.title}</h3>
                <p className="company-name">{job.company?.name}</p>
              </div>
              {job.priority === 'urgent' && <span className="badge-urgent">Urgent</span>}
            </div>
            <p className="job-description">{job.description}</p>
            <div className="job-meta">
              <span>📍 {job.location}</span>
              <span>💼 {job.level}</span>
              <span>🏢 {job.department}</span>
              {job.salaryRange && <span>💰 {job.salaryRange.currency} {job.salaryRange.min?.toLocaleString()} - {job.salaryRange.max?.toLocaleString()}</span>}
              {job.expectedStartDate && <span>📅 Start: {job.expectedStartDate}</span>}
            </div>
            <button className="btn-primary btn-sm" onClick={() => navigate(`/recruiter/recommend/${job._id}`)}>
              Recommend Candidate
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecommendForm() {
  const navigate = useNavigate();
  const jobId = window.location.pathname.split('/').pop();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', currentRole: '', currentCompany: '',
    linkedinUrl: '', yearsOfExperience: '', recruiterNotes: '', status: 'draft'
  });

  useEffect(() => {
    loadJob();
  }, []);

  const loadJob = async () => {
    try {
      const data = await jobs.getById(jobId);
      setJob(data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await recommendations.create({
        jobId,
        candidate: {
          name: formData.name, email: formData.email, phone: formData.phone,
          currentRole: formData.currentRole, currentCompany: formData.currentCompany,
          linkedinUrl: formData.linkedinUrl, yearsOfExperience: parseInt(formData.yearsOfExperience)
        },
        recruiterNotes: formData.recruiterNotes,
        status: formData.status
      });
      alert('Recommendation created successfully!');
      navigate('/recruiter/recommendations');
    } catch (err) {
      alert('Error creating recommendation');
    } finally {
      setLoading(false);
    }
  };

  if (!job) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h1>Recommend Candidate</h1>
      <div className="job-summary">
        <h3>{job.title}</h3>
        <p>{job.company?.name} • {job.location}</p>
        {job.salaryRange && <p>💰 {job.salaryRange.currency} {job.salaryRange.min?.toLocaleString()} - {job.salaryRange.max?.toLocaleString()}</p>}
        {job.expectedStartDate && <p>📅 Expected Start: {job.expectedStartDate}</p>}
      </div>
      <form onSubmit={handleSubmit} className="form-large">
        <h2>Candidate Information</h2>
        <div className="form-group">
          <label>Full Name *</label>
          <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
        </div>
        <div className="form-group">
          <label>Email *</label>
          <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Current Role *</label>
          <input type="text" value={formData.currentRole} onChange={(e) => setFormData({...formData, currentRole: e.target.value})} required />
        </div>
        <div className="form-group">
          <label>Current Company</label>
          <input type="text" value={formData.currentCompany} onChange={(e) => setFormData({...formData, currentCompany: e.target.value})} />
        </div>
        <div className="form-group">
          <label>LinkedIn URL</label>
          <input type="url" value={formData.linkedinUrl} onChange={(e) => setFormData({...formData, linkedinUrl: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Years of Experience</label>
          <input type="number" value={formData.yearsOfExperience} onChange={(e) => setFormData({...formData, yearsOfExperience: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Your Notes (Private) *</label>
          <textarea value={formData.recruiterNotes} onChange={(e) => setFormData({...formData, recruiterNotes: e.target.value})} required rows="4" placeholder="Why is this candidate a good fit?" />
        </div>
        <div className="form-group">
          <label>Status</label>
          <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
            <option value="draft">Save as Draft (Private)</option>
            <option value="submitted">Submit to Company</option>
          </select>
        </div>
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Create Recommendation'}</button>
        </div>
      </form>
    </div>
  );
}

function RecruiterRecommendations() {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    try {
      const data = await recommendations.getAll();
      setRecs(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (id) => {
    if (!window.confirm('Submit this recommendation to the company?')) return;
    try {
      await recommendations.submit(id);
      alert('Recommendation submitted successfully!');
      loadRecommendations();
    } catch (err) {
      alert('Error submitting recommendation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recommendation? This cannot be undone.')) return;
    try {
      const response = await fetch(`https://joyful-reflection-production-1049.up.railway.app/api/recommendations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        alert('Recommendation deleted!');
        loadRecommendations();
      }
    } catch (err) {
      alert('Error deleting recommendation');
    }
  };

  const startEdit = (rec) => {
    setEditingId(rec._id);
    setEditForm({
      name: rec.candidate.name,
      email: rec.candidate.email,
      phone: rec.candidate.phone || '',
      currentRole: rec.candidate.currentRole || '',
      currentCompany: rec.candidate.currentCompany || '',
      yearsOfExperience: rec.candidate.yearsOfExperience || '',
      linkedinUrl: rec.candidate.linkedinUrl || '',
      notes: rec.recruiterNotes
    });
  };

  const saveEdit = async (id) => {
    try {
      const response = await fetch(`https://joyful-reflection-production-1049.up.railway.app/api/recommendations/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          candidate: {
            name: editForm.name,
            email: editForm.email,
            phone: editForm.phone,
            currentRole: editForm.currentRole,
            currentCompany: editForm.currentCompany,
            yearsOfExperience: editForm.yearsOfExperience,
            linkedinUrl: editForm.linkedinUrl
          },
          recruiterNotes: editForm.notes
        })
      });
      if (response.ok) {
        setEditingId(null);
        alert('Updated successfully!');
        loadRecommendations();
      }
    } catch (err) {
      alert('Error updating');
    }
  };



  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h1>My Recommendations</h1>
      {recs.length === 0 ? (
        <div className="empty-state"><p>No recommendations yet</p></div>
      ) : (
        <div className="recs-list">
          {recs.map(rec => (
            <div key={rec._id} className="rec-card">
              <div onClick={() => setExpandedId(expandedId === rec._id ? null : rec._id)} style={{cursor: 'pointer'}}>
                <div className="rec-header">
                  <div>
                    <h3>{rec.candidate.name} {expandedId === rec._id ? '▼' : '▶'}</h3>
                    <p>{rec.candidate.currentRole}</p>
                  </div>
                  <span className={`badge-${rec.status}`}>{rec.status}</span>
                </div>
                <p><strong>For:</strong> {rec.job?.title} at {rec.job?.company?.name}</p>
              </div>
              
              {expandedId === rec._id && (
                <div style={{marginTop: '15px', padding: '15px', background: '#f3f4f6', borderRadius: '6px'}} onClick={(e) => e.stopPropagation()}>
                  {editingId === rec._id ? (
                    <div style={{display: 'grid', gap: '10px'}}>
                      <input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} placeholder="Name" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <input value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} placeholder="Email" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} placeholder="Phone" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <input value={editForm.currentRole} onChange={(e) => setEditForm({...editForm, currentRole: e.target.value})} placeholder="Current Role" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <input value={editForm.currentCompany} onChange={(e) => setEditForm({...editForm, currentCompany: e.target.value})} placeholder="Company" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <input value={editForm.yearsOfExperience} onChange={(e) => setEditForm({...editForm, yearsOfExperience: e.target.value})} placeholder="Years" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <input value={editForm.linkedinUrl} onChange={(e) => setEditForm({...editForm, linkedinUrl: e.target.value})} placeholder="LinkedIn" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} placeholder="Notes" rows="3" style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}} />
                      <div style={{display: 'flex', gap: '10px'}}>
                        <button className="btn-primary btn-sm" onClick={() => saveEdit(rec._id)}>Save</button>
                        <button className="btn-secondary btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p><strong>📧</strong> {rec.candidate.email}</p>
                      {rec.candidate.phone && <p><strong>📱</strong> {rec.candidate.phone}</p>}
                      {rec.candidate.currentCompany && <p><strong>🏢</strong> {rec.candidate.currentCompany}</p>}
                      {rec.candidate.yearsOfExperience && <p><strong>⏱️</strong> {rec.candidate.yearsOfExperience} years</p>}
                      {rec.candidate.linkedinUrl && <p><strong>🔗</strong> <a href={rec.candidate.linkedinUrl} target="_blank" rel="noopener noreferrer">LinkedIn</a></p>}
                      <p style={{marginTop: '10px'}}><strong>Notes:</strong> {rec.recruiterNotes}</p>
                      {rec.companyFeedback && <div style={{marginTop: '10px', padding: '10px', background: '#fef3c7', borderRadius: '4px'}}><strong>Company feedback:</strong> {rec.companyFeedback}</div>}
                    </>
                  )}
                </div>
              )}
              
              {rec.status === 'draft' && (
                <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                  <button className="btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); handleSubmit(rec._id); }}>Submit</button>
                  {!editingId && <button className="btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setExpandedId(rec._id); startEdit(rec); }}>Edit</button>}
                  <button className="btn-secondary btn-sm" style={{background: '#dc2626', color: 'white'}} onClick={(e) => { e.stopPropagation(); handleDelete(rec._id); }}>Delete</button>
                </div>
              )}
            </div>

          ))}
        </div>
      )}
    </div>
  );
}
function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const user = auth.getUser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://joyful-reflection-production-1049.up.railway.app/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      if (response.ok) {
        alert('Password changed successfully!');
        navigate(user.role === 'recruiter' ? '/recruiter/dashboard' : '/company/dashboard');
      } else {
        setError('Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Change Password</h1>
        <p style={{color: '#dc2626', marginBottom: '20px'}}>⚠️ You must change your password before continuing</p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Current Password</label>
            <input type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
          <div className="form-group">
            <label>New Password (min 8 characters)</label>
            <input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}



function AdminAddCompany() {
  const [formData, setFormData] = useState({
    adminSecret: '', companyName: '', contactEmail: '', contactName: '',
    tempPassword: '', description: '', sector: '', location: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    try {
      const response = await 
fetch('https://joyful-reflection-production-1049.up.railway.app/api/companies/admin/create', 
{
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Company created!');
        setFormData({ adminSecret: '', companyName: '', contactEmail: 
'', contactName: '', tempPassword: '', description: '', sector: '', 
location: '' });
      } else {
        setError(data.message || 'Error');
      }
    } catch (err) {
      setError('Server error');
    }
  };

  return (
    <div className="login-container">
      <div className="login-box" style={{maxWidth: '500px'}}>
        <h1>Add New Company</h1>
        {message && <div style={{padding: '10px', background: '#d1fae5', 
color: '#065f46', borderRadius: '4px', marginBottom: 
'15px'}}>{message}</div>}
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label>Admin Secret</label><input 
type="password" required value={formData.adminSecret} onChange={(e) => 
setFormData({...formData, adminSecret: e.target.value})} /></div>
          <div className="form-group"><label>Company Name</label><input 
type="text" required value={formData.companyName} onChange={(e) => 
setFormData({...formData, companyName: e.target.value})} /></div>
          <div className="form-group"><label>Contact Email</label><input 
type="email" required value={formData.contactEmail} onChange={(e) => 
setFormData({...formData, contactEmail: e.target.value})} /></div>
          <div className="form-group"><label>Contact Name</label><input 
type="text" required value={formData.contactName} onChange={(e) => 
setFormData({...formData, contactName: e.target.value})} /></div>
          <div className="form-group"><label>Temporary 
Password</label><input type="password" required 
value={formData.tempPassword} onChange={(e) => setFormData({...formData, 
tempPassword: e.target.value})} /></div>
          <div 
className="form-group"><label>Description</label><textarea 
value={formData.description} onChange={(e) => setFormData({...formData, 
description: e.target.value})} rows="3" /></div>
          <div className="form-group"><label>Sector</label><input 
type="text" value={formData.sector} onChange={(e) => 
setFormData({...formData, sector: e.target.value})} /></div>
          <div className="form-group"><label>Location</label><input 
type="text" value={formData.location} onChange={(e) => 
setFormData({...formData, location: e.target.value})} /></div>
          <button type="submit" className="btn-primary">Create 
Company</button>
        </form>
      </div>
    </div>
  );
}


function CompanyDashboard() {
  const [stats, setStats] = useState(null);
  const [jobsList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, jobsData] = await Promise.all([jobs.getStats(), jobs.getAll()]);
      setStats(statsData);
      setJobsList(jobsData.slice(0, 5));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h1>Company Dashboard</h1>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats?.openJobs || 0}</div>
          <div className="stat-label">Your Open Jobs</div>
        </div>
      </div>
      <div className="section">
        <div className="section-header">
          <h2>Your Job Postings</h2>
          <button className="btn-primary" onClick={() => navigate('/company/jobs/new')}>Post New Job</button>
        </div>
        <div className="jobs-list">
          {jobsList.map(job => (
            <div key={job._id} className="job-card">
              <div className="job-header">
                <div>
                  <h3>{job.title}</h3>
                  <p>{job.department} • {job.level}</p>
                </div>
                <span className={`badge-${job.status}`}>{job.status}</span>
              </div>
              <p>{job.description}</p>
              {job.salaryRange && <p className="job-meta">💰 {job.salaryRange.currency} {job.salaryRange.min?.toLocaleString()} - {job.salaryRange.max?.toLocaleString()}</p>}
              {job.expectedStartDate && <p className="job-meta">📅 Start: {job.expectedStartDate}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PostJobForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '', department: 'Engineering', level: 'Mid', location: '',
    remote: 'Hybrid', description: '', skills: '', priority: 'normal',
    salaryMin: '', salaryMax: '', salaryCurrency: 'USD',
    startMonth: '', startYear: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const jobData = {
        ...formData,
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s)
      };
      
      if (formData.salaryMin && formData.salaryMax) {
        jobData.salaryRange = {
          min: parseInt(formData.salaryMin),
          max: parseInt(formData.salaryMax),
          currency: formData.salaryCurrency
        };
      }
      
      if (formData.startMonth && formData.startYear) {
        jobData.expectedStartDate = `${formData.startMonth} ${formData.startYear}`;
      }
      
      await jobs.create(jobData);
      alert('Job posted successfully!');
      navigate('/company/dashboard');
    } catch (err) {
      alert('Error posting job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <h1>Post New Job</h1>
      <form onSubmit={handleSubmit} className="form-large">
        <div className="form-group">
          <label>Job Title *</label>
          <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required placeholder="e.g., Senior Software Engineer" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Department *</label>
            <select value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} required>
              <option>Engineering</option>
              <option>Product</option>
              <option>Marketing</option>
              <option>Sales</option>
            </select>
          </div>
          <div className="form-group">
            <label>Level *</label>
            <select value={formData.level} onChange={(e) => setFormData({...formData, level: e.target.value})} required>
              <option>Junior</option>
              <option>Mid</option>
              <option>Senior</option>
              <option>Lead</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Location *</label>
            <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required placeholder="e.g., San Francisco, CA" />
          </div>
          <div className="form-group">
            <label>Remote *</label>
            <select value={formData.remote} onChange={(e) => setFormData({...formData, remote: e.target.value})} required>
              <option>On-site</option>
              <option>Remote</option>
              <option>Hybrid</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Description *</label>
          <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required rows="4" />
        </div>
        <div className="form-group">
          <label>Skills (comma-separated)</label>
          <input type="text" value={formData.skills} onChange={(e) => setFormData({...formData, skills: e.target.value})} placeholder="e.g., React, Node.js" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Min Salary</label>
            <input type="number" value={formData.salaryMin} onChange={(e) => setFormData({...formData, salaryMin: e.target.value})} placeholder="e.g., 100000" />
          </div>
          <div className="form-group">
            <label>Max Salary</label>
            <input type="number" value={formData.salaryMax} onChange={(e) => setFormData({...formData, salaryMax: e.target.value})} placeholder="e.g., 150000" />
          </div>
        </div>
        <div className="form-group">
          <label>Currency</label>
          <select value={formData.salaryCurrency} onChange={(e) => setFormData({...formData, salaryCurrency: e.target.value})}>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
            <option value="AED">AED (د.إ)</option>
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Expected Start Month</label>
            <select value={formData.startMonth} onChange={(e) => setFormData({...formData, startMonth: e.target.value})}>
              <option value="">Select month</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
          </div>
          <div className="form-group">
            <label>Expected Start Year</label>
            <select value={formData.startYear} onChange={(e) => setFormData({...formData, startYear: e.target.value})}>
              <option value="">Select year</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Posting...' : 'Post Job'}</button>
        </div>
      </form>
    </div>
  );
}

function CompanyRecommendations() {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    try {
      const data = await recommendations.getAll();
      setRecs(data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (id) => {
    const feedback = window.prompt('Enter your feedback:');
    const status = window.prompt('Status (reviewed/shortlisted/interviewing/offered/rejected):');
    if (!feedback || !status) return;
    try {
      await recommendations.addFeedback(id, { feedback, status });
      alert('Feedback added successfully!');
      loadRecommendations();
    } catch (err) {
      alert('Error adding feedback');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h1>Candidate Recommendations</h1>
      {recs.length === 0 ? (
        <div className="empty-state"><p>No recommendations yet</p></div>
      ) : (
        <div className="recs-list">
          {recs.map(rec => (
            <div key={rec._id} className="rec-card">
              <div className="rec-header">
                <div>
                  <h3>{rec.candidate.name}</h3>
                  <p>{rec.candidate.currentRole}</p>
                </div>
                <span className={`badge-${rec.status}`}>{rec.status}</span>
              </div>
              <p><strong>For:</strong> {rec.job?.title}</p>
              <p><strong>Email:</strong> {rec.candidate.email}</p>
              {!rec.companyFeedback && (
                <button className="btn-primary btn-sm" onClick={() => handleFeedback(rec._id)}>Add Feedback</button>
              )}
              {rec.companyFeedback && (
                <div className="feedback"><strong>Your feedback:</strong> {rec.companyFeedback}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Nav() {
  const user = auth.getUser();
  const navigate = useNavigate();
  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };
  if (!user) return null;
  return (
    <nav className="navbar">
      <div className="nav-brand"><img 
src="/logo.png" alt="The Vault" style={{height: '140px'}} /></div>
      <div className="nav-links">
        {user.role === 'recruiter' ? (
          <>
            <Link to="/recruiter/dashboard">Dashboard</Link>
            <Link to="/recruiter/jobs">Jobs</Link>
            <Link to="/recruiter/recommendations">Recommendations</Link>
            <Link to="/recruiter/candidates">Candidates</Link>
          </>
        ) : (
          <>
            <Link to="/company/dashboard">Dashboard</Link>
            <Link to="/company/jobs/new">Post Job</Link>
            <Link to="/company/recommendations">Recommendations</Link>
          </>
        )}
        <button onClick={handleLogout} className="btn-logout">Logout</button>
      </div>
    </nav>
  );
}

function ProtectedRoute({ children, allowedRole }) {
  const user = auth.getUser();
  if (!user) return <Navigate to="/login" />;
  if (allowedRole && user.role !== allowedRole) return <Navigate to={`/${user.role}/dashboard`} />;
  return <><Nav />{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin-add-company" element={<AdminAddCompany />} 
/>
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/recruiter/dashboard" element={<ProtectedRoute allowedRole="recruiter"><RecruiterDashboard /></ProtectedRoute>} />
        <Route path="/recruiter/jobs" element={<ProtectedRoute allowedRole="recruiter"><RecruiterJobsList /></ProtectedRoute>} />
        <Route path="/recruiter/recommend/:id" element={<ProtectedRoute allowedRole="recruiter"><RecommendForm /></ProtectedRoute>} />
        <Route path="/recruiter/recommendations" element={<ProtectedRoute allowedRole="recruiter"><RecruiterRecommendations /></ProtectedRoute>} />
        <Route path="/recruiter/candidates" element={<ProtectedRoute allowedRole="recruiter"><CandidateDatabase /></ProtectedRoute>} />
        <Route path="/company/dashboard" element={<ProtectedRoute allowedRole="company"><CompanyDashboard /></ProtectedRoute>} />
        <Route path="/company/jobs/new" element={<ProtectedRoute allowedRole="company"><PostJobForm /></ProtectedRoute>} />
        <Route path="/company/recommendations" element={<ProtectedRoute allowedRole="company"><CompanyRecommendations /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
